import {Add} from '@mui/icons-material'
import {Box, CircularProgress, Divider, IconButton, Typography} from '@mui/material'
import {TFunction} from 'i18next'
import {trim} from 'lodash'
import React, {ReactNode, useState} from 'react'

import {CommentForm} from './CommentForm'
import {CommentItem} from './CommentItem'
import {CommonComment, EditCommentProps, DeleteCommentProps} from './comments.types'

type CommentsProps<T extends CommonComment> = {
  isLoading: boolean
  onAddComment?: (text: string) => void
  timeZone: string
  data?: T[]
  deleteCommentProps?: DeleteCommentProps
  editCommentProps?: EditCommentProps
  renderPrefix?: (commentId: string) => ReactNode | undefined
  hideDivider?: boolean
  translate: TFunction
  language: string
  dateFormat?: string
  showCounter?: boolean
  showHeader?: boolean
  showAdd?: boolean
  customCommentPostfix?: (comment: T) => ReactNode
}

export const Comments = <T extends CommonComment>({
  data,
  isLoading,
  onAddComment,
  deleteCommentProps,
  editCommentProps,
  renderPrefix,
  timeZone,
  hideDivider,
  translate,
  language,
  showCounter,
  showHeader = true,
  showAdd = true,
  dateFormat,
  customCommentPostfix
}: CommentsProps<T>) => {
  const [open, setOpen] = useState(false)

  return (
    <Box mt={showHeader || showAdd ? 3 : 0} data-test-id="comments-box">
      {(showHeader || showAdd) && (
        <Box display="flex" justifyContent="space-between" alignItems="center">
          {showHeader && (
            <Typography variant="button" data-test-id="comments-header">
              {translate('comments.cardTitle')}
              {showCounter && !!data?.length && ` (${data.length})`}
            </Typography>
          )}
          {showAdd && (
            <IconButton
              component="span"
              data-test-id="comments-add-button"
              color="primary"
              aria-label={translate('comments.label.addComment')}
              onClick={() => setOpen((op) => !op)}
            >
              {isLoading ? (
                <CircularProgress size={20} data-test-id="comments-loading-spinner" />
              ) : (
                <Add />
              )}
            </IconButton>
          )}
        </Box>
      )}
      {open && (
        <CommentForm
          onCancel={() => setOpen(false)}
          onSave={(value) => {
            setOpen(false)
            if (showAdd) {
              onAddComment!(trim(value))
            }
          }}
          translate={translate}
        />
      )}
      {data?.map((comment) => (
        <React.Fragment key={comment.id}>
          <CommentItem
            comment={comment}
            deleteCommentProps={deleteCommentProps}
            editCommentProps={editCommentProps}
            renderPrefix={renderPrefix}
            timeZone={timeZone}
            translate={translate}
            language={language}
            noTextGutter={hideDivider}
            dateFormat={dateFormat}
            customCommentPostfix={customCommentPostfix}
          />
          {!hideDivider && <Divider />}
        </React.Fragment>
      ))}
    </Box>
  )
}
